import { useEffect, useRef, useState } from 'react'
import type React from 'react'
import styles from './MapRow.module.css'
import { loadMapKit } from './loadMapKit'
import { useMapKitToken } from './useMapKitToken'
import type { MapKitMap } from '../types/mapkit'

export interface MapRowProps {
  stationName: string
  lat: number
  lng: number
}

const REGION_SPAN_DEG = 0.003

/**
 * Resolve the app's effective dark state for the Apple map's `colorScheme`.
 * Mirrors the app theme cascade: a forced `data-theme` (Night Tube sets
 * `dark`; nothing sets `light`) overrides the OS `prefers-color-scheme`.
 * MapKit JS does NOT auto-follow the app theme (unlike iOS/Android MKMapView),
 * so on web we must set it explicitly — otherwise a dark app shows a light map.
 */
function resolveMapDark(): boolean {
  if (typeof document === 'undefined') return false
  const forced = document.documentElement.dataset.theme
  if (forced === 'dark') return true
  if (forced === 'light') return false
  return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
}

export function MapRow({ stationName, lat, lng }: MapRowProps): React.ReactElement {
  const coords = `${lat.toFixed(4)}, ${lng.toFixed(4)}`
  const mapDivRef = useRef<HTMLDivElement | null>(null)
  const tokenEntry = useMapKitToken()
  const [mapReady, setMapReady] = useState(false)
  const centreRef = useRef<((lat: number, lng: number) => void) | null>(null)
  // The station the map must show, read by a map built later than the last
  // centring effect (see the rebuild note below).
  const stationRef = useRef({ lat, lng })

  // One map per token, not per station: building a MapKit map (a WebGL
  // context plus its shaders) on every station switch ran on the main
  // thread in the middle of the station camera pan. A station change only
  // re-centres it (the effect below).
  useEffect(() => {
    if (!tokenEntry || !mapDivRef.current) {
      return
    }
    let cancelled = false
    let map: MapKitMap | null = null
    let cleanupScheme: (() => void) | null = null
    loadMapKit()
      .then((mapkit) => {
        if (cancelled || !mapDivRef.current) {
          return
        }
        if (!mapkit.initialized) {
          mapkit.init({
            authorizationCallback: (done) => {
              done(tokenEntry.token)
            },
          })
        }
        map = new mapkit.Map(mapDivRef.current, {
          isScrollEnabled: false,
          isZoomEnabled: false,
          isRotationEnabled: false,
          showsCompass: mapkit.FeatureVisibility.Hidden,
          showsZoomControl: false,
          showsMapTypeControl: false,
          showsScale: mapkit.FeatureVisibility.Hidden,
          colorScheme: resolveMapDark() ? 'dark' : 'light',
        })
        const created = map
        centreRef.current = (centreLat: number, centreLng: number): void => {
          created.region = new mapkit.CoordinateRegion(
            new mapkit.Coordinate(centreLat, centreLng),
            new mapkit.CoordinateSpan(REGION_SPAN_DEG, REGION_SPAN_DEG),
          )
        }
        // Centre the new map now. A rebuild for a refreshed token runs its
        // cleanup's `setMapReady(false)` and this `setMapReady(true)` in one
        // batch, so `mapReady` never changes and the centring effect below
        // never fires — the map stayed on MapKit's default region, (0, 0)
        // off Africa (owner 2026-10-20, Royal Oak).
        centreRef.current(stationRef.current.lat, stationRef.current.lng)
        // Keep the map's colour scheme in lockstep with the app theme:
        // OS pref changes AND Night Tube's `data-theme` attribute flips.
        const applyScheme = (): void => {
          if (map) {
            map.colorScheme = resolveMapDark() ? 'dark' : 'light'
          }
        }
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        mql.addEventListener('change', applyScheme)
        const observer = new MutationObserver(applyScheme)
        observer.observe(document.documentElement, {
          attributes: true,
          attributeFilter: ['data-theme'],
        })
        cleanupScheme = (): void => {
          mql.removeEventListener('change', applyScheme)
          observer.disconnect()
        }
        setMapReady(true)
      })
      .catch(() => {
        // Stay on placeholder when MapKit JS fails to load.
      })
    return (): void => {
      cancelled = true
      if (cleanupScheme) {
        cleanupScheme()
      }
      if (map) {
        map.destroy()
      }
      centreRef.current = null
      setMapReady(false)
    }
  }, [tokenEntry])

  // Re-centres the map on every station change; a map built later centres
  // itself on `stationRef`, so this is a no-op until one exists.
  useEffect(() => {
    stationRef.current = { lat, lng }
    centreRef.current?.(lat, lng)
  }, [lat, lng])

  return (
    <div className={styles.container} data-testid="station-details-map">
      <div
        ref={mapDivRef}
        className={styles.map}
        data-testid="station-details-map-canvas"
        aria-hidden="true"
      />
      {!mapReady && (
        <div className={styles.placeholder} data-testid="station-details-map-placeholder">
          <div className={styles.content}>
            {/* Figma map glyph — a plain rounded-rect tile marker
                (30×22, 1.5px text-secondary stroke), not a pin. */}
            <span className={styles.glyph} aria-hidden="true" />
            <p className={styles.name}>{stationName}</p>
            <p className={styles.coords}>{coords}</p>
          </div>
        </div>
      )}
    </div>
  )
}
